import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
// MUI
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import Paper from '@mui/material/Paper';
// App Components

const useStyles = makeStyles()((theme: any) => ({
  container: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(1.5),
    paddingTop: theme.spacing(0),
  },
  paperContainer: {
    width: '100%',
    padding: theme.spacing(1),
    position: 'relative',
    zIndex: 1,
  },
  formInput: {
    '& .MuiInputBase-input': {
      padding: 0,
      ...theme.typography.paragraphRegular,
      overflow: 'auto !important' as 'auto',
      resize: 'none',
    },
    '& .MuiInputBase-multiline': {
      padding: theme.spacing(0.25),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

interface ChatControlsProps {
  handleSubmit: (e: React.FormEvent, message?: string) => void;
  input: string;
  isStreaming: boolean;
  isReady: boolean;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  recording?: boolean;
  startRecording?: () => void;
  stopRecording?: () => void;
  isLoading: boolean;
}

type ActionButtonProps = Omit<ChatControlsProps, 'setInput'>;

const ActionButton = ({
  input,
  handleSubmit,
  recording,
  isLoading,
  isReady,
  startRecording = undefined,
  stopRecording = undefined,
}: ActionButtonProps) => {
  if (Boolean(startRecording) && Boolean(stopRecording && input.length === 0)) {
    if (recording) {
      return (
        <IconButton variant='contained' size='small' onClick={() => stopRecording && stopRecording()}>
          <MicOffRoundedIcon />
        </IconButton>
      );
    } else {
      return (
        <IconButton
          onClick={() => {
            startRecording && startRecording();
          }}
          variant='contained'
          size='small'
        >
          <GraphicEqRoundedIcon />
        </IconButton>
      );
    }
  }

  return (
    <IconButton
      color='primary'
      variant='contained'
      size='small'
      disabled={isLoading || !isReady}
      type='submit'
      onClick={handleSubmit}
    >
      <ArrowUpwardRoundedIcon />
    </IconButton>
  );
};

const ChatControls = ({
  handleSubmit,
  input,
  setInput,
  recording,
  startRecording,
  stopRecording,
  isStreaming,
  isLoading,
  isReady,
}: ChatControlsProps) => {
  const { classes } = useStyles();
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const maxRows = 6;

  const syncTextareaHeight = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    const textarea = inputRef.current;
    if (!textarea || !(textarea instanceof HTMLTextAreaElement)) return;

    textarea.style.height = 'auto';
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight || '0');
    const paddingTop = parseFloat(computedStyle.paddingTop || '0');
    const paddingBottom = parseFloat(computedStyle.paddingBottom || '0');
    const borderTop = parseFloat(computedStyle.borderTopWidth || '0');
    const borderBottom = parseFloat(computedStyle.borderBottomWidth || '0');
    const maxHeight =
      lineHeight > 0 ? lineHeight * maxRows + paddingTop + paddingBottom + borderTop + borderBottom : undefined;
    const nextHeight = textarea.scrollHeight;
    const clampedHeight = maxHeight ? Math.min(nextHeight, maxHeight) : nextHeight;

    textarea.style.height = `${clampedHeight}px`;
    textarea.style.overflowY = nextHeight > clampedHeight ? 'auto' : 'hidden';
  }, [maxRows]);

  React.useEffect(() => {
    syncTextareaHeight();
  }, [input, syncTextareaHeight]);

  return (
    <Box className={classes.container}>
      <Paper elevation={16} className={classes.paperContainer}>
        <form onSubmit={handleSubmit}>
          <OutlinedInput
            autoFocus
            className={classes.formInput}
            fullWidth
            size='small'
            multiline
            rows={1}
            placeholder='What do you want to know?'
            value={input}
            inputComponent='textarea'
            inputRef={inputRef}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          <Box className={classes.footer}>
            <Typography variant='captionRegular' color='text.disabled'>
              Quicken Assist can make mistakes, so double-check it
            </Typography>
            <ActionButton
              input={input}
              handleSubmit={handleSubmit}
              isStreaming={isStreaming}
              isLoading={isLoading}
              isReady={isReady}
              recording={recording}
              startRecording={startRecording}
              stopRecording={stopRecording}
            />
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ChatControls;
